import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects'
import { sanitizeTelephone } from '../../helpers'
import { appsDocumentSelected, appsRegisterSelected } from '../../helpers/app'
import { ICognitoUserSession, IUser } from '../../models'
import { urls } from '../../routing'
import {
  AuthenticationService,
  LiteralsService,
  // ReservationService,
  UserService
} from '../../services'
import {
  changeUserLanguageAction,
  checkIfShowPaymentMethodAction,
  clearSignInFieldsAction,
  fetchClientInfoAction,
  setChangeLanguageAction,
  setDocumentsAction,
  setGuestModeAction,
  setIsLoadingUserInfoAction,
  // setIsUserExperienced,
  // setIsUserPenalizedAction,
  setNotificationAction,
  setPickedSeatsAction,
  setSignInPasswordAction,
  setSignInUsernameAction,
  setUserInfoAction,
  signInAction,
  signInSuccessfulAction
} from '../actions'
import { IStoreState } from '../states'
import { UserInfoTypeKeys } from '../types'
import { Application } from '../../config'
import { HttpStatusCode } from 'axios'

const { isIframe } = Application

// Worker Saga
function* checkUserAsync(): any {
  let tokens: ICognitoUserSession | null = null
  let user: IUser | null = null
  const routerHistory = yield getContext('routerHistory')

  try {
    tokens = yield call(AuthenticationService.getTokens)
    if (tokens) {
      user = yield call(UserService.getInfo)
    }
  } catch (error) {
    console.error(error)
  }

  if (tokens) {
    try {
      if (!user) {
        user = yield call(AuthenticationService.getUserAttributes)
      }
      if (!!tokens && !!user) {
        // INFO : Disabled penalties and experienced features on session check
        // let penalties = []

        // try {
        //   penalties = yield call(ReservationService.getFaultReservations)
        // } catch (error) {
        //   // tslint:disable-next-line: no-console
        //   console.error(error)
        // }

        // if (user.id) {
        //   try {
        //     const token = yield call(
        //       AuthenticationService.checkPermissionAndGetToken
        //     )

        //     yield call(UserService.sendDeviceToken, user.id, token)
        //   } catch (error) {
        //     // tslint:disable-next-line: no-console
        //     console.error(error)
        //   }
        // }

        // const { language } = yield select((storeState: IStoreState) => storeState.configuration)
        // if (user.id && user.locale !== language) {
        //    yield call(UserService.updateLanguage, user.id, language)
        // }
        yield put(setUserInfoAction(user))

        if (appsDocumentSelected?.requireDocumentation) {
          yield put({ type: UserInfoTypeKeys.FETCH_DOCUMENTS })
        }

        // fetch Client Info
        yield put(fetchClientInfoAction())
        // const isExperiended = yield call(UserService.getIsUserExperienced)
        // yield put(setIsUserExperienced(isExperiended))

        const { locale } = yield select((state: IStoreState) => state.userInfo)
        LiteralsService.setLanguage(locale)
        yield put(setChangeLanguageAction(locale))
        // if (penalties && penalties.length && penalties.length === 3) {
        //   yield put(setIsUserPenalizedAction())
        //   yield call(routerHistory.push, urls.penalties)
        // } else {
        const [seats, prmSeats] = user.prm ? [0, 1] : [1, 0];
        yield put(setPickedSeatsAction(seats, prmSeats))
        yield put(checkIfShowPaymentMethodAction())
        const { isShowNotificationModal } = yield select((storeState: IStoreState) => storeState.reservations)
        if (!isShowNotificationModal) {
          if (
            routerHistory.location.pathname === urls.signIn ||
            routerHistory.location.pathname === urls.newPasswordRequired ||
            routerHistory.location.pathname === urls.accountVerificationStep ||
            routerHistory.location.pathname === urls.recoverPasswordRestoreStep
          ) {
            yield call(routerHistory.push, urls.reservations)
          } else if (
            routerHistory.location.pathname === urls.newReservationBusStopsStep ||
            routerHistory.location.pathname === urls.newReservationDateStep ||
            routerHistory.location.pathname === urls.newReservationRouteOptionsStep ||
            routerHistory.location.pathname === urls.newReservationConfirmationStep
          ) {
            if (isIframe) {
              let townId = new URLSearchParams(location.search).get('townId')
              if (!townId) townId = routerHistory.location.state?.townId
              yield call(routerHistory.push, urls.newReservationBusStopsStep + `?townId=${townId}`)
            } else {
              yield call(routerHistory.push, urls.newReservationBusStopsStep)
            }
          } else if (routerHistory.location.pathname === '/') {
            yield call(routerHistory.push, urls.reservations)
          }
        }
      } else {
        yield call(routerHistory.replace, urls.signIn)
      }
    } catch (error: any) {
      if (error.status === HttpStatusCode.Unauthorized) {
        yield call(routerHistory.replace, urls.blocked)
      }
    }
  } else {
    const { isGuest } = yield select((state: IStoreState) => state.userInfo)
    if (isIframe) {
      yield put(setSignInUsernameAction(process.env[`REACT_APP_ADMIN_USER`]!))
      yield put(setSignInPasswordAction(process.env[`REACT_APP_ADMIN_PWD`]!))
      yield put(signInAction())
    }
    else if (!isGuest) {
      // Opening the bus stops step without a session is the same as pressing
      // "guest mode": the town comes from ?townId, or from the towns modal.
      const isBusStopsStep = routerHistory.location.pathname.startsWith(urls.newReservationBusStopsStep)

      if (isBusStopsStep) yield put(setGuestModeAction())
      else yield call(routerHistory.replace, urls.signIn)
    }
  }

  yield put(signInSuccessfulAction())
}

function* saveUserInfoAsync(): any {
  try {
    yield put(setIsLoadingUserInfoAction(true))
    const userInfo = { ...(yield select((state: IStoreState) => state.userInfo)) }
    const registerData = appsRegisterSelected?.data || []
    if (registerData.includes('additionalPhone') && userInfo.additionalPhone) {
      userInfo.additionalPhone = sanitizeTelephone(userInfo.additionalPhone, userInfo.additionalPhoneCallingCode)
    }
    if (registerData.includes('birthdate') && userInfo.birthDay && userInfo.birthMonth && userInfo.birthYear) {
      const day = userInfo.birthDay.length === 1 ? `0${userInfo.birthDay}` : userInfo.birthDay
      const month = userInfo.birthMonth.length === 1 ? `0${userInfo.birthMonth}` : userInfo.birthMonth
      userInfo.birthdate = `${userInfo.birthYear}-${month}-${day}`
    }
    yield call(UserService.updateInfo, userInfo)
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  } finally {
    yield put(setIsLoadingUserInfoAction(false))
  }
}

function* fetchDocumentsAsync(): any {
  try {
    const documents = yield call(UserService.getDocuments)
    yield put(setDocumentsAction(documents))
  } catch (error) {
    console.error(error)
  }
}

function* uploadDocumentAsync(action: any): any {
  try {
    yield put(setIsLoadingUserInfoAction(true))
    const { image, type, fileName } = action.payload
    const { email } = yield select((state: IStoreState) => state.userInfo)
    yield call(UserService.postDocument, image, type, fileName, email)
    const documents = yield call(UserService.getDocuments)
    yield put(setDocumentsAction(documents))
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  } finally {
    yield put(setIsLoadingUserInfoAction(false))
  }
}

function* deleteDocumentAsync(action: any): any {
  try {
    yield put(setIsLoadingUserInfoAction(true))
    yield call(UserService.deleteDocument, action.payload)
    const documents = yield call(UserService.getDocuments)
    yield put(setDocumentsAction(documents))
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  } finally {
    yield put(setIsLoadingUserInfoAction(false))
  }
}

function* saveUserLanguageAsync(action: any): any {
  const routerHistory = yield getContext('routerHistory')

  try {
    yield put(setIsLoadingUserInfoAction(true))
    yield put(changeUserLanguageAction(action.payload))
    const { id } = yield select((state: IStoreState) => state.userInfo)
    yield call(UserService.updateLanguage, id, action.payload)
    yield call(routerHistory.push, urls.configuration.configurationDisplay)
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  } finally {
    yield put(setIsLoadingUserInfoAction(false))
  }
}

// Watcher Saga:
function* watchCheckUserAsync() {
  yield takeLatest(UserInfoTypeKeys.CHECK_USER, checkUserAsync)
}

function* watchSaveUserInfoAsyc() {
  yield takeLatest(UserInfoTypeKeys.SAVE_USER_INFO, saveUserInfoAsync)
}

function* watchSaveLanguageAsyc() {
  yield takeLatest(UserInfoTypeKeys.SAVE_USER_LANGUAGE, saveUserLanguageAsync)
}

function* watchFetchDocumentsAsync() {
  yield takeLatest(UserInfoTypeKeys.FETCH_DOCUMENTS, fetchDocumentsAsync)
}

function* watchUploadDocumentAsync() {
  yield takeLatest(UserInfoTypeKeys.UPLOAD_DOCUMENT, uploadDocumentAsync)
}

function* watchDeleteDocumentAsync() {
  yield takeLatest(UserInfoTypeKeys.DELETE_DOCUMENT, deleteDocumentAsync)
}

// single entry point to start all Sagas at once
export default function* rootSaga() {
  yield all([
    watchCheckUserAsync(),
    watchSaveUserInfoAsyc(),
    watchSaveLanguageAsyc(),
    watchFetchDocumentsAsync(),
    watchUploadDocumentAsync(),
    watchDeleteDocumentAsync()
  ])
}
